import { createContext, useContext, useMemo } from 'react';

// Who is serving at THIS till, for the screens below it.
//
// Deliberately not Firebase Auth. Firebase keeps one signed-in user per app
// instance, persisted and shared by every tab on the origin, so holding the
// staff session there meant one client's till evicted the administrator — and
// any other client's till — from the same browser.
//
// Instead the till verifies the password on a throwaway auth instance (see
// verifyCredentials in utils/authServiceUtil.js) and keeps the verified
// address here, in React state belonging to one mounted screen. That is what
// makes the sessions independent: two tabs are two JS heaps, so /admin,
// /club-a and /club-b can each be signed in as someone different at
// the same time, and none of them can end another's session.
//
// The value is the acting address, which posUtil records as `performedBy` on
// the audit trail and `cashier` on each order.

// It also carries what that person MAY DO — the permissions of the role their
// client assigned them, resolved once at sign-in by fetchStaffPermissions.
// Held here rather than passed down because the checks are needed deep inside
// the screens (the export button, the place-order button), and threading a
// permission list through to them would mean every screen in between knew
// about permissions whether it used them or not.

const PosSessionContext = createContext({
  staffEmail: '',
  permissions: [],
  unrestricted: false,
  isStoreAdmin: false,
  roleName: '',
  can: () => false,
});

/**
 * Supplies the acting staff member, and their permissions, to a till's screens.
 *
 * @param {object} props
 * @param {string} props.staffEmail - the address this till verified.
 * @param {string[]} [props.permissions] - the permission keys their role grants.
 * @param {boolean} [props.unrestricted] - bypasses every check: a superuser, or
 *   an account with no role (see POS_UNASSIGNED_STAFF_UNRESTRICTED).
 * @param {string} [props.roleName] - named on screen, so a missing tab has a
 *   visible reason rather than looking like a bug.
 * @param {boolean} [props.isStoreAdmin] - the club's own administrator: may
 *   filter sales by staff and adjust the club's staff role rights.
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function PosSessionProvider({
  staffEmail,
  permissions = [],
  unrestricted = false,
  isStoreAdmin = false,
  roleName = '',
  children,
}) {
  // Rebuilt only when the session actually changes. A fresh object every
  // render would re-render every screen below on any parent render — on the
  // till that means the cart redrawing while someone is typing a quantity
  // into it.
  const value = useMemo(() => ({
    staffEmail,
    permissions,
    unrestricted,
    isStoreAdmin,
    roleName,
    can: (permission) => unrestricted || permissions.includes(permission),
  }), [staffEmail, permissions, unrestricted, isStoreAdmin, roleName]);

  return (
    <PosSessionContext.Provider value={value}>
      {children}
    </PosSessionContext.Provider>
  );
}

/**
 * The address serving this till.
 *
 * Returns '' outside a provider rather than throwing: posUtil falls back to
 * the signed-in account and then to 'Unknown', so a missing provider costs a
 * vague audit entry rather than a crashed sale.
 *
 * `can(permission)` is the one every screen should use — it already accounts
 * for an unrestricted session, which `permissions` alone does not. Outside a
 * provider it answers false, so a screen mounted without one hides its
 * controls rather than offering them to nobody in particular.
 *
 * @returns {{staffEmail: string, permissions: string[], unrestricted: boolean,
 *   isStoreAdmin: boolean, roleName: string, can: (permission: string) => boolean}}
 */
export function usePosSession() {
  return useContext(PosSessionContext);
}

export default PosSessionContext;
